import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

/**
 * SPEC 4.8 / 5.8 (BW12.5 hotfix):
 *  - Desktop: centered modal (max-width based on `placement` prop).
 *  - Mobile (<768px): bottom-sheet covering ~85vh, with drag handle + drag-to-dismiss.
 *  - Drawer panel widths: small=420, medium=560, large=720 (configurable).
 *  - safe-area-inset-bottom padding so the iOS home-indicator doesn't sit on
 *    top of the close button.
 *  - 220ms ease-out slide-in; backdrop fades to dim.
 *  - Esc / overlay tap to dismiss.
 *
 * The previous right-anchored variant (`placement="right"`) is preserved for
 * any caller that explicitly opts in (e.g. very wide multi-column workspaces).
 */

type DrawerSize = "sm" | "md" | "lg";
type DrawerPlacement = "center" | "bottom-sheet" | "right";

type DrawerProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  ariaLabel?: string;
  /**
   * Layout on desktop (>=768px). Defaults to "center" — a centered modal.
   * Mobile always falls back to a bottom sheet regardless of this value.
   *  - "center": centered modal, max-width follows `size`
   *  - "right": legacy right-side anchored panel
   *  - "bottom-sheet": bottom sheet on every viewport
   */
  placement?: DrawerPlacement;
  /**
   * Max width on desktop center placement.
   *  - sm = 420px, md = 560px, lg = 720px
   * Defaults to "md".
   */
  size?: DrawerSize;
};

const SIZE_PX: Record<DrawerSize, number> = {
  sm: 420,
  md: 560,
  lg: 720,
};

export function Drawer({
  open,
  onClose,
  title,
  children,
  ariaLabel,
  placement = "center",
  size = "md",
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);

  // BW12.5 hotfix Issue 8: keep onClose in a ref so the open-effect doesn't
  // depend on a value that changes every render. Previously every keystroke
  // re-ran the effect — its cleanup re-focused `previousActive`, then the
  // new effect re-focused the panel, stealing focus from the input the user
  // was typing into.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    // Lock background scroll while the sheet is open so the page doesn't
    // bounce around when the user drags inside the dialog (iOS Safari).
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus trap entry — only steal focus once on mount.
    const previousActive = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      previousActive?.focus?.();
    };
  }, [open]);

  function onTouchStart(e: React.TouchEvent<HTMLDivElement>) {
    if (window.matchMedia("(min-width: 768px)").matches) return;
    dragStartY.current = e.touches[0].clientY;
  }
  function onTouchMove(e: React.TouchEvent<HTMLDivElement>) {
    if (dragStartY.current == null) return;
    const dy = e.touches[0].clientY - dragStartY.current;
    if (dy > 0) setDragOffset(dy);
  }
  function onTouchEnd() {
    if (dragStartY.current == null) return;
    const dy = dragOffset;
    dragStartY.current = null;
    if (dy > 100) {
      setDragOffset(0);
      onClose();
    } else {
      setDragOffset(0);
    }
  }

  if (!open) return null;
  if (typeof document === "undefined") return null;

  const isCenter = placement === "center";
  const isRight = placement === "right";
  const isBottomSheet = placement === "bottom-sheet";
  const desktopMaxWidth = SIZE_PX[size];

  // Outer container layout — center for modal, flex-end for right-side legacy
  const justifyContent = isRight ? "flex-end" : "center";
  const alignItems = isCenter ? "center" : isBottomSheet ? "flex-end" : "stretch";

  // Panel sizing varies based on placement on desktop. Mobile CSS overrides
  // these via the `.haus-drawer-panel` media query at the bottom of this file.
  const panelStyle: React.CSSProperties = isRight
    ? {
        position: "relative",
        background: "var(--bg-3)",
        borderLeft: "1px solid var(--hair-2)",
        width: "min(var(--drawer-w, 420px), 100vw)",
        maxWidth: "100vw",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        animation: `haus-slide-right var(--dur-drawer) var(--ease-out)`,
        outline: "none",
        transform: dragOffset > 0 ? `translateY(${dragOffset}px)` : undefined,
        transition: dragOffset > 0 ? "none" : "transform 180ms var(--ease-out)",
      }
    : isBottomSheet
      ? {
          position: "relative",
          background: "var(--bg-3)",
          borderTop: "1px solid var(--hair-2)",
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          width: "100%",
          maxWidth: "100vw",
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          animation: `haus-slide-up var(--dur-sheet, var(--dur-drawer)) var(--ease-out)`,
          outline: "none",
          transform: dragOffset > 0 ? `translateY(${dragOffset}px)` : undefined,
          transition: dragOffset > 0 ? "none" : "transform 180ms var(--ease-out)",
        }
      : {
          position: "relative",
          background: "var(--bg-3)",
          border: "1px solid var(--hair-2)",
          borderRadius: 16,
          width: `min(${desktopMaxWidth}px, calc(100vw - 32px))`,
          maxWidth: "100vw",
          maxHeight: "min(90vh, 920px)",
          display: "flex",
          flexDirection: "column",
          animation: `haus-pop-in var(--dur-drawer) var(--ease-out)`,
          outline: "none",
          transform: dragOffset > 0 ? `translateY(${dragOffset}px)` : undefined,
          transition: dragOffset > 0 ? "none" : "transform 180ms var(--ease-out)",
          boxShadow:
            "0 24px 48px -16px rgba(0,0,0,0.6), 0 8px 16px -8px rgba(0,0,0,0.4)",
        };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel ?? title ?? "Detail"}
      data-placement={placement}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "flex",
        justifyContent,
        alignItems,
        padding: isCenter ? "var(--space-4)" : 0,
      }}
    >
      <button
        type="button"
        aria-label="Close drawer"
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          border: "none",
          padding: 0,
          cursor: "pointer",
          animation: `haus-fade-in var(--dur-drawer) var(--ease-out)`,
        }}
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        className="haus-drawer-panel"
        data-placement={placement}
        data-size={size}
        style={panelStyle}
      >
        <div
          className="haus-drawer-handle"
          aria-hidden
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
        >
          <span />
        </div>
        <header
          className="haus-drawer-header"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchEnd}
        >
          <h2
            style={{
              fontSize: "var(--type-body-l)",
              fontFamily: "var(--font-sans)",
              fontWeight: 600,
            }}
          >
            {title ?? "Detail"}
          </h2>
          <button
            type="button"
            aria-label="Close drawer"
            onClick={onClose}
            style={{
              width: 36,
              height: 36,
              borderRadius: "var(--radius-card)",
              background: "transparent",
              color: "var(--fg-2)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={20} strokeWidth={1.5} />
          </button>
        </header>
        <div className="haus-drawer-body">{children}</div>
      </div>

      <style>{`
        @keyframes haus-fade-in {
          from { opacity: 0 } to { opacity: 1 }
        }
        @keyframes haus-slide-right {
          from { transform: translateX(100%); opacity: 0.6 }
          to   { transform: translateX(0%);   opacity: 1   }
        }
        @keyframes haus-slide-up {
          from { transform: translateY(100%); opacity: 0.6 }
          to   { transform: translateY(0%);   opacity: 1   }
        }
        @keyframes haus-pop-in {
          from { transform: translateY(8px) scale(0.98); opacity: 0 }
          to   { transform: translateY(0)    scale(1);    opacity: 1 }
        }
        .haus-drawer-handle { display: none; }
        .haus-drawer-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: var(--space-4) var(--space-6);
          border-bottom: 1px solid var(--hair);
        }
        .haus-drawer-body {
          flex: 1;
          overflow: auto;
          padding: var(--space-6);
          padding-bottom: calc(var(--space-6) + env(safe-area-inset-bottom, 0px));
        }
        @media (max-width: 767px) {
          /*
           * Mobile collapse: every drawer placement becomes a bottom-sheet.
           * Drag handle visible, drag-to-dismiss, ~85vh height.
           */
          .haus-drawer-panel {
            width: 100vw !important;
            max-width: 100vw !important;
            height: 85vh !important;
            max-height: 85vh !important;
            margin-top: auto;
            border-left: none !important;
            border-right: none !important;
            border-bottom: none !important;
            border-top: 1px solid var(--hair-2) !important;
            border-radius: 16px 16px 0 0 !important;
            animation: haus-slide-up var(--dur-sheet, var(--dur-drawer)) var(--ease-out) !important;
            touch-action: pan-y;
            box-shadow: none !important;
          }
          .haus-drawer-handle {
            display: flex;
            justify-content: center;
            padding: 8px 0 4px;
            cursor: grab;
            touch-action: none;
          }
          .haus-drawer-handle span {
            width: 36px;
            height: 4px;
            background: var(--hair-2);
            border-radius: 999px;
          }
          .haus-drawer-header {
            padding: var(--space-3) var(--space-5) var(--space-3);
          }
          .haus-drawer-body {
            padding: var(--space-5) var(--space-5);
            padding-bottom: calc(var(--space-6) + env(safe-area-inset-bottom, 0px));
          }
        }
        @media (max-width: 767px) {
          /* Bottom-sheet placement gets a drag handle on mobile. */
          .haus-drawer-panel[data-placement="bottom-sheet"] .haus-drawer-handle {
            display: flex;
          }
        }
        @media (min-width: 768px) {
          /* Desktop: bottom-sheet placement still hugs the bottom. */
          .haus-drawer-panel[data-placement="bottom-sheet"] {
            width: min(640px, 100vw);
            max-width: 100vw;
            max-height: 80vh;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .haus-drawer-panel {
            animation: none !important;
          }
        }
      `}</style>
    </div>,
    document.body,
  );
}
